import { IcoRemove, InfoIcon } from '@/utils/icons'
import Modal from '@common/Modal'
import TextField from '../../components/TextField/TextField'
import { useEffect, useMemo, useState } from 'react'
import SecondaryButton from '../../components/SecondaryButton/SecondaryButton'
import { Spin, Switch } from 'antd'
import { LoadingOutlined } from '@ant-design/icons'
import { useAddAlert } from '@/hooks/useAddAlert'
import { directionGroupApi, usersAPI } from '@/api'
import SelectField from '../../components/SelectField/SelectField'
import cn from './index.module.css'
import CheckboxViolet from '@UI/New/ChecboxViolet/CheckboxViolet'
import TooltipWrapper from '@UI/New/TooltipWrapper/TooltipWrapper'
import { useGetAdditionalSettings } from '../../components/AdditionalCourseSettings/hooks/useGetAdditionalSettings'
import { groupName } from '../../components/AdditionalCourseSettings/utils/fetchAdditionalSettings'
import classNames from 'classnames'
import { useAsyncFn, useDebounce } from 'react-use'
import { useGetUserProfile } from '@/hooks/useGetUserProfile'
import rolesEnum from '@/constant/rolesEnum'
import Preloader from '@UI/Preloader'
import Text from '@UI/New/Text'
import { useGetGroupName } from '@pages/Admin/PlatformSettings2/AddSettingsEmployeesPersonAccount/hooks/useGetGroupName'
import {
  getTeacherSalaryTooltip,
  TEACHER_SALARY_OPTIONS,
} from '@pages/Admin/Manage/tabs/GroupsPage/utils/teacherSalary'
import { TeacherSalaryType } from '@pages/Admin/Manage/tabs/GroupsPage/types'
import useFranchises from '@/hooks/useFranchises'

const DEFAULT_SALARY_TYPE: TeacherSalaryType = 'per-lesson'
const ADDITIONAL_TEACHERS_TOOLTIP =
  'Если вы включите данный тумблер, вы сможете добавить любого педагога в качестве дополнительного к этой группе. У дополнительного педагога будут появляться все те же домашние работы, контрольные и пробники, которые задал основной педагог. Дополнительные педагоги смогут проверять эти работы, а также задавать собственные работы в этой группе.'

const normalizeNonNegativeNumberInput = (value: string) => {
  if (!value) return ''
  return value.replace('-', '')
}

const CreateLearningGroup = ({
  isVisible,
  directionId,
  allowedFranchiseIds,
  onDismiss = () => {},
  onCreate = (group) => {},
}: {
  isVisible: boolean
  directionId: number
  allowedFranchiseIds?: number[]
  onDismiss: () => void
  onCreate: (folder: IGroup) => void
}) => {
  return (
    <Modal
      active={isVisible}
      setActive={onDismiss}
      // @ts-ignore
      className={cn.modalWrapper}
    >
      <CreateLearningGroupBody
        directionId={directionId}
        allowedFranchiseIds={allowedFranchiseIds}
        onCreate={onCreate}
        onDismiss={onDismiss}
      />
    </Modal>
  )
}

const CreateLearningGroupBody = ({
  directionId,
  allowedFranchiseIds,
  onDismiss = () => {},
  onCreate = (group) => {},
}: {
  directionId: number
  allowedFranchiseIds?: number[]
  onDismiss: () => void
  onCreate: (group: IGroup) => void
}) => {
  const addAlert = useAddAlert()
  const [lead, setLead] = useState<number | null>(null)
  const [franchiseId, setFranchiseId] = useState<number | null>(null)
  const [name, setName] = useState('')
  const [isSaving, setIsSaving] = useState(false)
  const [error, setError] = useState<string[]>([])
  const [maxStudents, setMaxStudents] = useState<number | null>(null)
  const [applyMaxStudents, setApplyMaxStudents] = useState<boolean>(false)
  const [hasAdditionalTeachers, setHasAdditionalTeachers] = useState(false)
  const [additionalTeacherIds, setAdditionalTeacherIds] = useState<string[]>([])
  const [calculateTeacherSalary, setCalculateTeacherSalary] = useState(false)
  const [salaryType, setSalaryType] =
    useState<TeacherSalaryType>(DEFAULT_SALARY_TYPE)
  const [teacherSalary, setTeacherSalary] = useState('0')
  const [durationMinutes, setDurationMinutes] = useState('')
  const [minAmountEnabled, setMinAmountEnabled] = useState(false)
  const [minAmount, setMinAmount] = useState('')
  const {
    enabled: franchisesEnabled,
    options: franchiseOptions,
    autoSelect: autoSelectFranchise,
    loading: franchisesLoading,
  } = useFranchises()
  const availableFranchiseOptions = useMemo(() => {
    if (allowedFranchiseIds === undefined) return franchiseOptions

    const allowedIds = allowedFranchiseIds.map(Number)
    return franchiseOptions.filter((option) =>
      allowedIds.includes(Number(option.value)),
    )
  }, [allowedFranchiseIds, franchiseOptions])
  const defaultFranchiseId = useMemo(() => {
    if (
      autoSelectFranchise &&
      availableFranchiseOptions.some(
        (option) => Number(option.value) === Number(autoSelectFranchise),
      )
    ) {
      return Number(autoSelectFranchise)
    }

    return availableFranchiseOptions.length === 1
      ? Number(availableFranchiseOptions[0].value)
      : null
  }, [autoSelectFranchise, availableFranchiseOptions])

  useEffect(() => {
    if (!franchisesEnabled) {
      setFranchiseId(null)
      return
    }
    if (
      franchiseId &&
      availableFranchiseOptions.some(
        (option) => Number(option.value) === Number(franchiseId),
      )
    ) {
      return
    }
    setFranchiseId(defaultFranchiseId)
    setLead(null)
    setAdditionalTeacherIds([])
  }, [
    availableFranchiseOptions,
    defaultFranchiseId,
    franchiseId,
    franchisesEnabled,
  ])

  const createGroup = () => {
    if (isSaving) return
    if (franchisesEnabled && !franchiseId) {
      setError(['franchise'])
      return
    }
    if (!lead) {
      setError(['lead'])
      return
    }
    if (!name) {
      setError(['name'])
      return
    }
    if (hasAdditionalTeachers && !additionalTeacherIds.length) {
      setError(['additionalTeachers'])
      return
    }
    if(applyMaxStudents){
      if(!maxStudents || !Number.isInteger(maxStudents) || (!!maxStudents && maxStudents < 0)) {
        setError(['maxStudents'])
        return
      }
    }
    if (calculateTeacherSalary) {
      const salary = Number(String(teacherSalary).replace(',', '.'))

      if (!Number.isFinite(salary) || salary < 0) {
        setError(['teacherSalary'])
        return
      }

      if (salaryType === 'per-duration') {
        const duration = Number(String(durationMinutes).replace(',', '.'))

        if (!Number.isFinite(duration) || duration <= 0) {
          setError(['durationMinutes'])
          return
        }
      }

      if (salaryType === 'per-student-hour' && minAmountEnabled) {
        const minSalary = Number(String(minAmount).replace(',', '.'))

        if (!Number.isFinite(minSalary) || minSalary < 0) {
          setError(['minAmount'])
          return
        }
      }
    }
    setIsSaving(true)

    const data = {
      direction_id: directionId,
      franchise_id: franchisesEnabled ? franchiseId : null,
      teacher_id: lead,
      name: name,
      max_students : applyMaxStudents ? maxStudents : null,
      additional_teacher_ids: hasAdditionalTeachers
        ? additionalTeacherIds.map(Number)
        : [],
      suffix_type: additionalSettings?.random_group_names_in_course ? randomName?.suffix_type : null,
      type: calculateTeacherSalary ? salaryType : DEFAULT_SALARY_TYPE,
      salary: calculateTeacherSalary
        ? Number(String(teacherSalary).replace(',', '.'))
        : 0,
      ...(calculateTeacherSalary && salaryType === 'per-duration'
        ? {
            duration_minutes: Number(String(durationMinutes).replace(',', '.')),
          }
        : {}),
      ...(calculateTeacherSalary && salaryType === 'per-student-hour'
        ? {
            duration_minutes: 60,
            min_amount_enabled: minAmountEnabled,
            min_amount: minAmountEnabled
              ? Number(String(minAmount).replace(',', '.'))
              : null,
          }
        : {}),
    }
    directionGroupApi
      .add({ data })
      .then((res) => {
        if (res?.data?.data?.id) {
          addAlert({ title: 'Группа создана!' })
          onCreate(res?.data?.data)
        } else {
          addAlert({
            type: 'error',
            title:
              'Упс! Ошибка. Попробуйте ещё раз или обратитесь к администратору',
          })
        }
      })
      .catch((error) => {
        addAlert({
          type: 'error',
          title:
            'Упс! Ошибка. Попробуйте ещё раз или обратитесь к администратору',
        })
      })
      .finally(() => setIsSaving(false))
  }

  const {
      additionalSettings,
      loadingAdditionalSettings
  } = useGetAdditionalSettings({directionId: directionId})

  const applyMaxStudentsInDirection = (!!additionalSettings?.max_students || additionalSettings?.max_students === 0)
  
  useEffect(() => {
    if(applyMaxStudentsInDirection){
      setApplyMaxStudents(applyMaxStudentsInDirection)
      setMaxStudents(additionalSettings?.max_students)
    }
    else{
      setApplyMaxStudents(applyMaxStudentsInDirection)
      setMaxStudents(null)
    }
  }, [additionalSettings?.max_students])

  const [randomName, setRandomName] = useState<groupName | null>(null);
  useEffect(()=>{
    if(additionalSettings?.random_group_names_in_course){
      const randomIndex = Math.floor(Math.random() * additionalSettings?.groups_names?.length);
      setRandomName(additionalSettings?.groups_names[randomIndex])
      setName(additionalSettings?.groups_names[randomIndex]?.name ?? '')
    }
    else{
      setRandomName(null)
      setName('')
    }
  }, [additionalSettings])
  
  const [searchName, setSearchName] = useState('')
  const [colleaguesState, getColleaguesState] = useAsyncFn(
    async (name?: string) =>
      usersAPI.searchUsersForSelect({
        employeesOnly: 1,
        'per-page': '1000',
        ...(franchiseId ? { franchise_id: franchiseId } : {}),
        ...(name ? { name } : {}),
      }),
    [franchiseId],
  )

  useDebounce(() => {
    if (franchisesEnabled && !franchiseId) return
    getColleaguesState(searchName || undefined)
  }, 500, [searchName, franchiseId, franchisesEnabled])

  const allColleagues = (colleaguesState.value?.data?.data ?? [])
    ?.map((item: ISelect) => ({
      label: item.label,
      value: item.value.toString(),
    }))

  const getExpandForGetUser = useMemo(() => ["roles"], [])
      
  const { profile: profileSelectedEmployee, loading: loadingProfileSelectedEmployee } = useGetUserProfile({userId: lead, expand: getExpandForGetUser, isGetSeminarianDirections: true });
  const isIncludesRoleSeminarian: boolean = profileSelectedEmployee?.roles?.some(el => el.item_name === rolesEnum.seminarian) ?? false;
  const isChosenCourseAvailableWithChosenTeacher: boolean = profileSelectedEmployee?.seminarianDirections?.some(el => el.direction_id === directionId) ?? false;
  const { groupNamePlural } = useGetGroupName()
  const salaryTypeOptions = useMemo(() => TEACHER_SALARY_OPTIONS, [])
  const salaryTypeTooltip = getTeacherSalaryTooltip(salaryType)
  const salaryLabel =
    salaryType === 'per-student-hour'
      ? 'Впишите зп за одного человека'
      : 'Впишите зарплату за одно занятие'
  const showDurationField = salaryType === 'per-duration'
  const showMinAmountFields = salaryType === 'per-student-hour'

  return (
    <div className={cn.modalContainer}>
      <div className={cn.modalHeader}>
        <div className={cn.modalTitleContainer}>
          <span className={cn.modalHeaderTitle}>
            Создание группы/потока обучения
          </span>
        </div>
        <div className={cn.closeIcon} onClick={onDismiss}>
          <IcoRemove />
        </div>
      </div>
      <div className={cn.content}>
        {franchisesEnabled && (
          <SelectField
            label="Филиал"
            placeholder="Выберите филиал"
            value={franchiseId?.toString() ?? null}
            onChange={(value) => {
              setFranchiseId(value ? Number(value) : null)
              setLead(null)
              setAdditionalTeacherIds([])
            }}
            options={availableFranchiseOptions.map((option) => ({
              ...option,
              value: String(option.value),
            }))}
            className={cn.select}
            isInvalid={error.includes('franchise')}
            loading={franchisesLoading}
            disabled={
              availableFranchiseOptions.length === 1 && !!franchiseId
            }
            notFoundContent="Доступные филиалы не найдены"
          />
        )}
        <SelectField
          label="Выберите сотрудника"
          placeholder="сотрудник"
          value={lead?.toString()}
          onChange={(value) => {
            const teacherId = Number(value)
            setLead(teacherId)
            setAdditionalTeacherIds((ids) =>
              ids.filter((id) => Number(id) !== teacherId),
            )
          }}
          // @ts-ignore
          options={allColleagues}
          className={cn.select}
          isInvalid={error.includes('lead')}
          loading={colleaguesState.loading}
          disabled={franchisesEnabled && !franchiseId}
          onSearch={(value) => setSearchName(value)}
          onBlur={() => setSearchName('')}
          notFoundContent={
            franchisesEnabled
              ? 'В данной франшизе нет педагогов'
              : 'Педагоги не найдены'
          }
        />
        {
          loadingProfileSelectedEmployee ? (
            <Preloader align='center' />
          ) : (!isIncludesRoleSeminarian && !!lead) ? (
            <Text
              texts={
                [`У выбранного сотрудника нет роли педагога, она добавится ему автоматически при добавлении ему этой ${groupNamePlural?.toLowerCase()}`]
              } 
            />
          ) : (!isChosenCourseAvailableWithChosenTeacher && !!lead && !!directionId) ? (
            <Text
              texts={[`У этого сотрудника нет доступа к этому курсу. При создании с ним группы система автоматически добавит ему доступ к курсу.`]} 
            />
          ) : (<></>)
        }
        <div className={cn.switchWrapper}>
          <Switch
            checked={hasAdditionalTeachers}
            onChange={(checked) => {
              setHasAdditionalTeachers(checked)
              if (!checked) {
                setAdditionalTeacherIds([])
              }
            }}
          />
          <div>У данной группы есть дополнительные педагоги</div>
          <TooltipWrapper title={ADDITIONAL_TEACHERS_TOOLTIP}>
            <InfoIcon />
          </TooltipWrapper>
        </div>
        {hasAdditionalTeachers && (
          <SelectField
            label="Дополнительные педагоги"
            mode="multiple"
            placeholder="Выберите педагогов"
            value={additionalTeacherIds}
            onChange={(value) =>
              setAdditionalTeacherIds(Array.isArray(value) ? value : [])
            }
            options={allColleagues.filter(
              (option: ISelect) => Number(option.value) !== Number(lead),
            )}
            className={cn.select}
            isInvalid={error.includes('additionalTeachers')}
            loading={colleaguesState.loading}
            disabled={franchisesEnabled && !franchiseId}
            notFoundContent={
              franchisesEnabled
                ? 'В данной франшизе нет сотрудников'
                : 'Сотрудники не найдены'
            }
          />
        )}
        <TooltipWrapper title={additionalSettings?.random_group_names_in_course ? 'Вы не можете изменить название, оно подстввляется рандомно из возможных вариантов' : ''}>
          <div className={classNames({[cn.wrapperInputName]: additionalSettings?.random_group_names_in_course && randomName?.suffix_type === 'group_id'})}>
            <TextField
              label="Введите название группы"
              value={name}
              disabled={additionalSettings?.random_group_names_in_course}
              onChange={setName}
              className={classNames(cn.textField, {[cn.inputName]: additionalSettings?.random_group_names_in_course && randomName?.suffix_type === 'group_id'})}
              isInvalid={error.includes('name')}
            />
            {
                additionalSettings?.random_group_names_in_course && randomName?.suffix_type === 'group_id' && (
                    <div className={cn.newGroupNameId}>: {`{id}`}</div>
                )
            }
          </div>
        </TooltipWrapper>
        <div className={cn.switchWrapper}>
          <Switch
            checked={calculateTeacherSalary}
            onChange={(checked) => {
              setCalculateTeacherSalary(checked)
              setSalaryType(DEFAULT_SALARY_TYPE)
              setTeacherSalary((value) => checked ? value : '0')
              setDurationMinutes('')
              setMinAmountEnabled(false)
              setMinAmount('')
            }}
          />
          <div>Рассчитывать зарплату педагога за проведенные занятия</div>
        </div>

        {calculateTeacherSalary && (
          <>
            <SelectField
              label="Тип зарплаты педагога"
              value={salaryType}
              onChange={(value) => {
                setSalaryType((value || DEFAULT_SALARY_TYPE) as TeacherSalaryType)
                setDurationMinutes('')
                setMinAmountEnabled(false)
                setMinAmount('')
              }}
              options={salaryTypeOptions}
              className={cn.select}
            />
            {salaryTypeTooltip && (
              <div className={cn.salaryInfo}>{salaryTypeTooltip}</div>
            )}
            <TextField
              label={salaryLabel}
              value={teacherSalary}
              onChange={(value) =>
                setTeacherSalary(normalizeNonNegativeNumberInput(value))
              }
              type='number'
              className={cn.textField}
              isInvalid={error.includes('teacherSalary')}
            />
            {showDurationField && (
              <TextField
                label="Длительность занятия в минутах"
                value={durationMinutes}
                onChange={(value) =>
                  setDurationMinutes(normalizeNonNegativeNumberInput(value))
                }
                type='number'
                className={cn.textField}
                isInvalid={error.includes('durationMinutes')}
              />
            )}
            {showMinAmountFields && (
              <>
                <div className={cn.tooltipWrapper}>
                  <CheckboxViolet
                    value={minAmountEnabled}
                    /*@ts-ignore*/
                    setValue={setMinAmountEnabled}
                    text='Добавить минимальную гарантированную сумму'
                  />
                </div>
                {minAmountEnabled && (
                  <TextField
                    label="Минимальная сумма"
                    value={minAmount}
                    onChange={(value) =>
                      setMinAmount(normalizeNonNegativeNumberInput(value))
                    }
                    type='number'
                    className={cn.textField}
                    isInvalid={error.includes('minAmount')}
                  />
                )}
              </>
            )}
          </>
        )}
        <div className={cn.tooltipWrapper}>
          
          <TooltipWrapper title={applyMaxStudentsInDirection ? 'Вы не можете изменить это значение, оно настрено для всех групп этого курса' : ''}>
              <div>
                  <CheckboxViolet 
                    value={applyMaxStudents} 
                    /*@ts-ignore*/
                    setValue={ (applyMaxStudentsInDirection || loadingAdditionalSettings) ? () => {} : setApplyMaxStudents } 
                    text='Ограничить количество учащихся' />
              </div>
          </TooltipWrapper>
          
          <TooltipWrapper title={`Для группы можно задать максимальное число учащихся. Когда это число будет достигнуто, система не будет позволять добавлять новых учащихся`}>
            <InfoIcon />
          </TooltipWrapper>
        </div>
        
        {
          applyMaxStudents && (
          <TooltipWrapper title={applyMaxStudentsInDirection ? 'Вы не можете изменить это значение, оно настрено для всех групп этого курса' : ''}>
           <div>
              <TextField
                label="Укажите максимальное количество учащихся в группе"
                value={maxStudents ? maxStudents.toString() : ''}
                onChange={(value) => setMaxStudents(Number(value))}
                type='number'
                className={cn.textField}
                isInvalid={error.includes('maxStudents')}
                disabled={applyMaxStudentsInDirection || loadingAdditionalSettings}
              />
           </div>
          </TooltipWrapper>
          )
        }
      </div>
      <div className={cn.footer}>
        {isSaving && (
          <Spin
            indicator={
              <LoadingOutlined
                spin
                onPointerEnterCapture={undefined}
                onPointerLeaveCapture={undefined}
              />
            }
          />
        )}
        <SecondaryButton onClick={createGroup} disabled={isSaving || loadingAdditionalSettings}>
          Создать
        </SecondaryButton>
      </div>
    </div>
  )
}

export default CreateLearningGroup
